import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { AppView } from 'design-system/AppView';
import { rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, } from 'design-system/values';
import { SettingsItem, SettingsItemNavigate } from 'design-system/SettingsItem';
import Spacer from 'design-system/Spacer';
import StatusBar from 'design-system/StatusBar';
import useColors from 'hooks/useColors';
import SectionHeader from 'design-system/SectionHeader';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import IconSpace from 'design-system/icons/IconSpace';
import ListEmptyView from 'design-system/ListEmptyView';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import useStyles from 'hooks/useStyles';
import { selectUser } from 'reducers/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { IconBell, IconTrash } from 'design-system/icons';
import createStyleSheets from 'utils/createStyleSheets';
import { Indicator, SettingsSection, TextButtonLink } from 'design-system';
import IconPlus from 'design-system/icons/IconPlus';
import Modal from 'utils/packages/Modal';
import Button from 'design-system/Button';
import { useMarginBottom } from 'hooks/useMarginBottom';
import AlertView from 'design-system/AlertView';
import IconInfoTriangle from 'design-system/icons/IconInfoTriangle';
import { benefits, tierThemes } from 'features/premium/benefits';
import iconSource from 'design-system/img/notifications.png';
import { muteAll, useFetchOld, useTimeout } from 'hooks';
import PaymentToastModal from 'features/pay/components/toast/PaymentToastModal';
import { UpgradeWebModal } from 'design-system/UpgradeWebModal';
import { Tier } from 'features/premium/entities';
import { isUnlocked } from 'utils';
import ExtraMembersIntroCard from 'features/premium/components/ExtraMembersIntroCard';
import { selectExtraMembersInfo, selectIsExtraMembersFeatureAvailable, } from 'reducers/selectors/sharedPlans';
import SpaceMemberItem from './containers/SpaceMemberItem';
import { useDeleteSpace, useSpaceInfo } from './hooks';
import { isWeb } from '../../constants';
import { fetchSpacesAction, sendSpaceReminder, setSpaceName } from './actions';
import { ShareSpaceModal } from './components/ShareSpaceModal';
import { useSpaceAccounts } from './hooks/useSpaceAccounts';
import { AccountItem } from './components';
import { selectSpaceInvitesWithStatus } from './reducers/selectors';
import SpaceMemberInvited from './containers/SpaceMemberInvited';
import { useAddSpaceAccountModal } from './hooks/useAddSpaceAccountModal';
import useMoveSpaceModal from './hooks/useMoveSpaceModal';
const strings = {
    alertTitleOwner: 'Emma Ultimate has expired',
    alertTitleMember: 'This space is no longer available',
    alertSubtitleOwner: `Your ${tierThemes[benefits.spaces.minTier].name} subscription has expired. If you want to continue using Spaces, you need to upgrade your current plan.`,
    alertSubtitleMember: (firstName) => `${firstName || 'The space owner'} hasn’t renewed their ${tierThemes[benefits.spaces.minTier].name} subscription, so you no longer have access to this space`,
};
const EditSpace = withAnimatedNavigationHeader(({ route, navigation, onScroll }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const dispatch = useAppDispatch();
    const contentContainerStyles = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    const { pending: pendingInvites, rejected: rejectedInvites } = useAppSelector((store) => selectSpaceInvitesWithStatus(store, route.params.id));
    const user = useAppSelector(selectUser);
    const propsSpace = route.params?.space;
    const [spaceInfo, spaceInfoBusy, fetch] = useSpaceInfo(route.params.id ?? -1);
    const space = spaceInfo?.space ?? propsSpace;
    const { currentSpaceAccounts: accounts } = useSpaceAccounts(spaceInfo);
    const { showMoveSpaceToast, moveAccountsToast } = useMoveSpaceModal();
    const onAddAccount = useAddSpaceAccountModal(spaceInfo, false, showMoveSpaceToast);
    const onRemoved = useCallback(() => {
        navigation.pop();
    }, []);
    const [removeBusy, remove] = useDeleteSpace(route.params.id, onRemoved);
    const onDelete = useCallback(() => {
        Modal.showConfirmation({
            title: 'Are you sure?',
            subtitle: 'Do you want to delete this space? This action cannot be undone. Any accounts you or members added here will be moved to their Private spaces.',
            confirmButtonTitle: 'No, thanks',
            cancelButtonTitle: 'Yes, let me delete',
            onPressCancel: remove,
        });
    }, [remove]);
    const isOwner = space?.createdBy === user.id;
    const isAvailableSpace = space?.isDefault ||
        space?.isPremium ||
        (isOwner && isUnlocked(user, benefits.spaces.minTier));
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: isOwner
                ? () => (<NavigationHeaderRightIconButton onPress={onDelete}>
                {removeBusy ? (<Indicator dark/>) : (<IconTrash color={colors.text.primary}/>)}
              </NavigationHeaderRightIconButton>)
                : undefined,
        });
    }, [colors.text.primary, onDelete, isOwner, removeBusy]);
    const showValidationAlert = () => Modal.showConfirmation({
        title: 'Sorry, can’t use that 😭',
        subtitle: 'Enter 3 symbols minimum.',
        confirmButtonTitle: 'Okay',
    });
    const onEditName = useCallback(() => {
        if (!removeBusy && space)
            navigation.navigateDeprecated('TextFieldPage', {
                title: 'Space name',
                placeholder: 'Name your space...',
                defaultValue: space.name,
                buttonTitle: 'Save',
                textFieldProps: {
                    autoCapitalize: 'words',
                    keyboardType: 'default',
                },
                onPressNext: (value, setIsFetching) => {
                    if (value.length < 3) {
                        showValidationAlert();
                    }
                    else {
                        setIsFetching(true);
                        dispatch(setSpaceName(space.id, value));
                        navigation.pop();
                    }
                },
            });
    }, [removeBusy, space]);
    const onEditAccounts = () => {
        if (!removeBusy)
            navigation.navigateDeprecated('EditSpaceAccounts', {
                spaceInfo,
                onMoveAccounts: showMoveSpaceToast,
            });
    };
    const onUpgrade = useCallback(() => {
        if (isWeb && benefits.spaces.minTier !== Tier.free) {
            UpgradeWebModal.show(benefits.spaces.minTier);
        }
        else {
            navigation.navigateDeprecated('EmmaProModal', {
                benefitId: 'spaces',
                tier: Tier.ultimate,
                isTierRequired: true,
            });
        }
    }, []);
    const onShare = useCallback(() => {
        if (removeBusy || !space)
            return;
        if (!isAvailableSpace) {
            Modal.showConfirmation({
                title: 'You can’t add members to this space',
                subtitle: 'If you want to add members and continue using this space, you need to upgrade your current plan.\n\nDo you want to upgrade?',
                confirmButtonTitle: 'No, thanks',
                cancelButtonTitle: 'Yes, upgrade plan',
                onPressCancel: onUpgrade,
            });
            return;
        }
        ShareSpaceModal.show({
            fromEdit: true,
            space,
        });
    }, [isAvailableSpace, onUpgrade, removeBusy, space]);
    const [, , , fetchSendReminder] = useFetchOld(muteAll);
    const [showToast, setShowToast] = useState(false);
    useTimeout(() => setShowToast(false), showToast ? 3000 : undefined);
    const onSendReminder = useCallback(async () => {
        if (!space?.id)
            return;
        const res = await fetchSendReminder(sendSpaceReminder(space.id, 'SPACE_UPGRADE_REMINDER'));
        if (!res)
            return;
        setShowToast(true);
    }, [fetchSendReminder, space?.id]);
    const onAddAccountPress = useCallback(() => {
        if (isAvailableSpace) {
            onAddAccount();
            return;
        }
        if (isOwner) {
            Modal.showConfirmation({
                title: 'You can’t add accounts to this space',
                subtitle: 'If you want to add accounts and continue using this space, you need to upgrade your current plan.\n\nDo you want to upgrade?',
                confirmButtonTitle: 'No, thanks',
                cancelButtonTitle: 'Yes, upgrade plan',
                onPressCancel: onUpgrade,
            });
            return;
        }
        Modal.showConfirmation({
            title: 'You can’t add accounts to this space',
            subtitle: 'If you want to add accounts and continue using this space, contact the owner of the space to upgrade their plan',
            confirmButtonTitle: 'Remind them to upgrade',
            cancelButtonTitle: 'Okay',
            onConfirm: onSendReminder,
            confirmButtonLeftIcon: <IconBell color={colors.text.textOnBrand}/>,
        });
    }, [
        colors.text.textOnBrand,
        isAvailableSpace,
        isOwner,
        onAddAccount,
        onSendReminder,
        onUpgrade,
    ]);
    const owner = useMemo(() => spaceInfo?.users?.find(({ id }) => space?.createdBy === id), [space?.createdBy, spaceInfo?.users]);
    const onSpaceMemberRemoved = useCallback((it) => {
        if (it.id === user.id) {
            dispatch(fetchSpacesAction());
            navigation.pop();
        }
        else
            fetch();
    }, [user.id]);
    const isExtraMembersFeatureAvailable = useAppSelector(selectIsExtraMembersFeatureAvailable);
    const extraMembersInfo = useAppSelector(selectExtraMembersInfo);
    const showExtraMembersIntroCard = isExtraMembersFeatureAvailable &&
        space !== undefined &&
        !(extraMembersInfo && !extraMembersInfo.canAddSlot);
    const suggestNumOfExtraMemberSlots = useMemo(() => {
        let n = 0;
        if (spaceInfo?.users) {
            n += spaceInfo.users.length;
        }
        n += pendingInvites.length;
        return Math.max(n - 1, 0);
    }, [spaceInfo?.users, pendingInvites.length]);
    return (<AppView type="secondary" withFrame={false}>
        <PaymentToastModal isVisible={showToast} onCloseAction={() => setShowToast(false)} title="Sent a reminder to upgrade" message={`To ${owner?.firstName || 'owner'}`} iconSource={iconSource} style={styles.toast}/>
        <StatusBar barStyle="dark-content" animated/>
        <ScrollView onScroll={onScroll} scrollEventThrottle={16} contentContainerStyle={contentContainerStyles} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          {!spaceInfoBusy && !space ? (<>
              <Spacer.H16 />
              <ListEmptyView onCard title="Space not found"/>
            </>) : (<View style={styles.content}>
              {!isAvailableSpace && (<AlertView icon={<IconInfoTriangle colorKey="statusNegative"/>} iconSize={44} squircleColor={colors.elements.statusNegativeLight} title={isOwner ? strings.alertTitleOwner : strings.alertTitleMember} subTitle={isOwner
                    ? strings.alertSubtitleOwner
                    : strings.alertSubtitleMember(owner?.firstName)} containerStyles={styles.unavailableAlertContainer} style={styles.unavailableAlert}>
                  {isOwner ? (<Button errorPrimary square hug title="Upgrade" onPress={onUpgrade}/>) : null}
                </AlertView>)}
              <SettingsSection style={styles.settingsSection}>
                <SettingsItemNavigate description="Name" title={space?.name ?? ''} icon={<IconSpace color={colors.elements.brand}/>} onPress={removeBusy || !isOwner ? undefined : onEditName} disabled={!isOwner}/>
              </SettingsSection>
              <SectionHeader horizontalMargins largeVerticalMargins>
                Members
              </SectionHeader>
              <View style={styles.subContainer}>
                {isOwner ? (<View style={isWeb ? styles.hover : undefined}>
                    <SettingsItem onPress={onShare} title="Add members" iconSize={44} icon={<IconPlus gradientKey="ultimate"/>}/>
                  </View>) : null}
                {spaceInfo?.users?.map((it) => (<React.Fragment key={`${it.id}-${it.handle}`}>
                    <Spacer h={4}/>
                    <SpaceMemberItem spaceId={space?.id ?? -1} ownerId={space?.createdBy ?? -1} member={it} disabled={removeBusy} onRemoved={() => onSpaceMemberRemoved(it)}/>
                  </React.Fragment>))}
                {pendingInvites.map((invite) => (<SpaceMemberInvited key={invite.invite.id} spaceId={space?.id ?? -1} spaceCreatedBy={space?.createdBy ?? -1} invite={invite.invite} member={invite.member} status={'PENDING'} spaceName={space?.name ?? ''}/>))}
                {rejectedInvites.map((invite) => (<SpaceMemberInvited key={invite.invite.id} spaceId={space?.id ?? -1} spaceCreatedBy={space?.createdBy ?? -1} invite={invite.invite} member={invite.member} status={'REJECTED'} spaceName={space?.name ?? ''}/>))}
              </View>
              <SectionHeader button={<TextButtonLink onPress={onEditAccounts} text="Manage"/>} horizontalMargins largeVerticalMargins>
                Accounts
              </SectionHeader>
              <View style={styles.subContainer}>
                {accounts.length > 0 ? (<View style={isWeb ? styles.hover : undefined}>
                    <SettingsItem onPress={onAddAccountPress} title="Add account" iconSize={44} icon={<IconPlus gradientKey="ultimate"/>}/>
                    {accounts.map((item) => (<React.Fragment key={item.id}>
                        <AccountItem disabled account={item}/>
                      </React.Fragment>))}
                  </View>) : (<ListEmptyView style={styles.emptyContainer} compact title="No accounts" subTitle="You don’t have any accounts in this space">
                    <Button brandReversed hug medium onPress={onAddAccountPress} title="Add account"/>
                  </ListEmptyView>)}
              </View>
              {showExtraMembersIntroCard ? (<>
                  <SectionHeader horizontalMargins largeVerticalMargins>
                    Add members into your plan
                  </SectionHeader>
                  <ExtraMembersIntroCard suggestSpaceId={space?.id} suggestNumOfSlots={suggestNumOfExtraMemberSlots}/>
                </>) : null}
            </View>)}
        </ScrollView>
        {moveAccountsToast()}
      </AppView>);
}, ({ colors }) => ({
    title: 'Space settings',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets((colors) => ({
    subContainer: {
        backgroundColor: colors.cards.onDark,
        borderRadius: rem(16),
        marginHorizontal: rem(16),
        overflow: 'hidden',
    },
    content: {
        flex: 1,
        flexDirection: 'column',
        paddingTop: rem(16),
    },
    hover: {
        padding: 8,
    },
    settingsSection: { marginTop: 0 },
    emptyContainer: {
        padding: rem(24),
        backgroundColor: colors.cards.onDark,
        marginHorizontal: rem(16),
        borderRadius: rem(16),
    },
    unavailableAlertContainer: {
        marginTop: 0,
    },
    unavailableAlert: {
        paddingHorizontal: rem(16),
    },
    toast: {
        paddingTop: rem(16),
        marginTop: 0,
    },
}));
export default EditSpace;
